import type { Id } from "@repo/convex/dataModel";
import { ChevronDown, CircleAlert, UserPlus, UserRoundCheck } from "lucide-react";
import { useState } from "react";
import { ClaudiaIcon } from "@/components/claudia-icon";
import { OwnerSearchPopover } from "@/components/owner-search-popover";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { resolveUser, useOrgUserMap } from "@/hooks";

interface AssignmentBarProps {
  ownerId: Id<"users"> | null | undefined;
  onAssign: (userId: Id<"users">) => void | Promise<void>;
  disabled?: boolean;
}

export function AssignmentBar({ ownerId, onAssign, disabled = false }: AssignmentBarProps) {
  const userMap = useOrgUserMap();
  const [open, setOpen] = useState(false);

  const handleSelect = (userId: string) => {
    setOpen(false);
    void onAssign(userId as Id<"users">);
  };

  if (ownerId) {
    const owner = resolveUser(userMap, ownerId);
    const OwnerIcon = owner.systemKey === "claudia" ? ClaudiaIcon : UserRoundCheck;
    return (
      <Popover open={open} onOpenChange={(next) => !disabled && setOpen(next)}>
        <PopoverTrigger
          render={
            <button
              type="button"
              aria-label="Reassign owner"
              // Test contract: lets a reassignment pick someone other than the current owner
              // by id (a same-owner pick is a legitimate no-op that writes no audit row).
              data-owner-id={ownerId}
              disabled={disabled}
              className="flex w-full items-center gap-2.5 border-y border-blue-border bg-surface-blue px-3.5 py-2.5 text-left transition-colors hover:bg-blue-border/40 disabled:cursor-not-allowed disabled:opacity-60"
            />
          }
        >
          <OwnerIcon className="size-5 text-blue-content" />
          <span className="flex flex-1 flex-col gap-px min-w-0">
            <span className="text-[10px] font-medium text-muted-foreground">Assigned to</span>
            <span className="truncate text-xs font-semibold text-blue-content">
              {owner.fullName}
            </span>
          </span>
          <ChevronDown className="size-3.5 text-blue-content" />
        </PopoverTrigger>
        <PopoverContent align="end" className="w-80 p-0">
          <OwnerSearchPopover userMap={userMap} onSelect={handleSelect} />
        </PopoverContent>
      </Popover>
    );
  }

  return (
    <div className="flex w-full items-center gap-2.5 border-y border-amber-border bg-surface-amber px-3.5 py-2.5">
      <CircleAlert className="size-5 text-amber-content" />
      <span className="flex flex-1 flex-col gap-px min-w-0">
        <span className="text-[11px] font-semibold text-amber-content">No owner assigned</span>
        <span className="truncate text-[11px] text-amber-content">Assign someone to respond</span>
      </span>
      <Popover open={open} onOpenChange={(next) => !disabled && setOpen(next)}>
        <PopoverTrigger
          render={
            <button
              type="button"
              aria-label="Assign owner"
              disabled={disabled}
              className="flex items-center gap-1 border border-amber-border bg-amber-border/40 px-2.5 py-1 text-[11px] font-semibold text-amber-content transition-colors hover:bg-amber-border/60 disabled:cursor-not-allowed disabled:opacity-60"
            />
          }
        >
          <UserPlus className="size-3 text-amber-content" />
          Assign
        </PopoverTrigger>
        <PopoverContent align="end" className="w-80 p-0">
          <OwnerSearchPopover userMap={userMap} onSelect={handleSelect} />
        </PopoverContent>
      </Popover>
    </div>
  );
}
